import { useEffect, useState } from "react";
import {
  Badge,
  Banner,
  BlockStack,
  Button,
  Card,
  InlineGrid,
  Select,
  SkeletonBodyText,
  Text,
  TextField,
} from "@shopify/polaris";
import { useAuthenticatedFetch } from "../hooks/useAuthenticatedFetch";
import { useLanguage } from "../providers/LanguageProvider";
import type {
  ApiResponse,
  BillingProfileResponse,
  MerchantBillingProfile,
} from "../types";

const EMPTY_PROFILE: MerchantBillingProfile = {
  businessType: "company",
  legalName: "",
  taxId: "",
  addressLine1: "",
  addressLine2: "",
  city: "",
  region: "",
  postalCode: "",
  countryCode: "",
};

export default function BillingProfileCard({
  onStateChange,
}: {
  onStateChange: (complete: boolean, loading: boolean) => void;
}) {
  const authenticatedFetch = useAuthenticatedFetch();
  const { t } = useLanguage();
  const [profile, setProfile] = useState<MerchantBillingProfile>(EMPTY_PROFILE);
  const [complete, setComplete] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    authenticatedFetch("/api/billing/profile")
      .then(async (response) => {
        const json =
          (await response.json()) as ApiResponse<BillingProfileResponse>;
        if (!response.ok || !json.success || !json.data)
          throw new Error(json.error);
        if (!active) return;
        setProfile(json.data.profile);
        setComplete(json.data.complete);
        onStateChange(json.data.complete, false);
      })
      .catch(() => {
        if (!active) return;
        setError(t.billingProfile.loadError);
        onStateChange(false, false);
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [authenticatedFetch, onStateChange, t.billingProfile.loadError]);

  function set<K extends keyof MerchantBillingProfile>(
    key: K,
    value: MerchantBillingProfile[K],
  ) {
    setProfile((current) => ({ ...current, [key]: value }));
    setComplete(false);
    setMessage(null);
    onStateChange(false, false);
  }

  async function save() {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const response = await authenticatedFetch("/api/billing/profile", {
        method: "PATCH",
        body: JSON.stringify(profile),
      });
      const json =
        (await response.json()) as ApiResponse<BillingProfileResponse>;
      if (!response.ok || !json.success || !json.data) {
        setError(
          json.error === "invalid_billing_profile"
            ? t.billingProfile.invalid
            : t.billingProfile.saveError,
        );
        return;
      }
      setProfile(json.data.profile);
      setComplete(true);
      setMessage(t.billingProfile.saved);
      onStateChange(true, false);
    } catch {
      setError(t.billingProfile.saveError);
    } finally {
      setSaving(false);
    }
  }

  return (
    <Card>
      <BlockStack gap="400">
        <div
          style={{ display: "flex", justifyContent: "space-between", gap: 16 }}
        >
          <BlockStack gap="100">
            <Text as="h2" variant="headingLg">
              {t.billingProfile.title}
            </Text>
            <Text as="p" tone="subdued">
              {t.billingProfile.description}
            </Text>
          </BlockStack>
          <Badge tone={complete ? "success" : "attention"}>
            {complete ? t.billingProfile.complete : t.billingProfile.required}
          </Badge>
        </div>

        {loading ? (
          <SkeletonBodyText lines={6} />
        ) : (
          <>
            <Select
              label={t.billingProfile.businessType}
              requiredIndicator
              options={[
                { label: t.billingProfile.company, value: "company" },
                { label: t.billingProfile.soleTrader, value: "sole_trader" },
              ]}
              value={profile.businessType}
              onChange={(value) =>
                set(
                  "businessType",
                  value as MerchantBillingProfile["businessType"],
                )
              }
            />
            <TextField
              label={
                profile.businessType === "company"
                  ? t.billingProfile.legalNameCompany
                  : t.billingProfile.legalNameSoleTrader
              }
              requiredIndicator
              autoComplete="organization"
              value={profile.legalName}
              onChange={(value) => set("legalName", value)}
            />
            <TextField
              label={t.billingProfile.taxId}
              requiredIndicator
              autoComplete="off"
              value={profile.taxId}
              onChange={(value) => set("taxId", value)}
            />
            <TextField
              label={t.billingProfile.addressLine1}
              requiredIndicator
              autoComplete="address-line1"
              value={profile.addressLine1}
              onChange={(value) => set("addressLine1", value)}
            />
            <TextField
              label={t.billingProfile.addressLine2}
              autoComplete="address-line2"
              value={profile.addressLine2}
              onChange={(value) => set("addressLine2", value)}
            />
            <InlineGrid columns={{ xs: 1, sm: 2 }} gap="400">
              <TextField
                label={t.billingProfile.city}
                requiredIndicator
                autoComplete="address-level2"
                value={profile.city}
                onChange={(value) => set("city", value)}
              />
              <TextField
                label={t.billingProfile.region}
                autoComplete="address-level1"
                value={profile.region}
                onChange={(value) => set("region", value)}
              />
              <TextField
                label={t.billingProfile.postalCode}
                requiredIndicator
                autoComplete="postal-code"
                value={profile.postalCode}
                onChange={(value) => set("postalCode", value)}
              />
              <TextField
                label={t.billingProfile.countryCode}
                requiredIndicator
                helpText={t.billingProfile.countryHelp}
                autoComplete="country"
                maxLength={2}
                value={profile.countryCode}
                onChange={(value) => set("countryCode", value.toUpperCase())}
              />
            </InlineGrid>
            <div>
              <Button variant="primary" loading={saving} onClick={save}>
                {saving ? t.billingProfile.saving : t.billingProfile.save}
              </Button>
            </div>
          </>
        )}

        {message && <Banner tone="success">{message}</Banner>}
        {error && (
          <Banner tone="critical" onDismiss={() => setError(null)}>
            {error}
          </Banner>
        )}
      </BlockStack>
    </Card>
  );
}
